<template>
  <div>
    <div class="feature-allowlist">
      <div class="feature-allowlist__header">
        <b>Enabled Formatting</b>
        <p class="feature-allowlist__hint">
          Choose exactly which formatting tools appear in this field's
          toolbar. Leave everything checked for the full editor.
        </p>
        <div class="feature-allowlist__actions">
          <a @click="selectAll">Select All</a>
          <span> · </span>
          <a @click="selectNone">Select None</a>
        </div>
      </div>

      <v-checkbox
        v-for="feature in features"
        :key="feature.key"
        :label="feature.label"
        :input-value="isEnabled(feature.key)"
        @change="toggle(feature.key, $event)"
        hide-details
      ></v-checkbox>
    </div>

    <div class="typography-variants" v-if="isEnabled('typography')">
      <div class="typography-variants__header">
        <b>Typography Classes</b>
        <p class="typography-variants__hint">
          Adds a dropdown to this field's toolbar for switching a block of
          text between these styles. Each renders with the given HTML tag
          and CSS class. "Body" is always available and needs no setup here
          — it's just a plain paragraph with no class.
        </p>
      </div>

      <div
        class="typography-variant"
        v-for="(variant, idx) in localValue.typography_variants"
        :key="idx"
      >
        <v-text-field
          label="Label"
          placeholder="e.g. Statement"
          v-model="variant.label"
          hide-details
        ></v-text-field>
        <v-combobox
          label="Tag"
          v-model="variant.tag"
          :items="tagOptions"
          hide-details
        ></v-combobox>
        <v-text-field
          label="Class"
          placeholder="e.g. text--statement"
          v-model="variant.class"
          hide-details
        ></v-text-field>
        <v-btn icon @click="deleteVariant(idx)">
          <v-icon>$delete</v-icon>
        </v-btn>
      </div>
      <v-btn block outlined @click="addVariant">+ Add Typography Class</v-btn>
    </div>
  </div>
</template>

<script>
import { RICH_TEXT_FEATURES, RICH_TEXT_FEATURE_KEYS } from "../../richtext/features";

export default {
  props: {
    value: Object,
  },
  data() {
    return {
      features: RICH_TEXT_FEATURES,
      tagOptions: [
        "p",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "span",
        "div",
        "blockquote",
      ],
      localValue: {
        ...this.value,
        // everything enabled by default, matching the pre-allowlist default
        features: this.value.features || [...RICH_TEXT_FEATURE_KEYS],
        typography_variants: (this.value.typography_variants || []).map(
          (variant) => ({ ...variant })
        ),
      },
    };
  },
  methods: {
    isEnabled(key) {
      return this.localValue.features.includes(key);
    },
    toggle(key, enabled) {
      const idx = this.localValue.features.indexOf(key);
      if (enabled && idx === -1) this.localValue.features.push(key);
      if (!enabled && idx !== -1) this.localValue.features.splice(idx, 1);
    },
    selectAll() {
      this.localValue.features = [...RICH_TEXT_FEATURE_KEYS];
    },
    selectNone() {
      this.localValue.features = [];
    },
    addVariant() {
      this.localValue.typography_variants.push({
        label: "",
        tag: "p",
        class: "",
      });
    },
    deleteVariant(idx) {
      this.localValue.typography_variants.splice(idx, 1);
    },
    // stable identifier for a variant, independent of its (editable) label.
    // Content stores this as the block's `variant` attribute, so changing
    // it after content exists would orphan any text already saved under
    // the old key - derive it from the class name instead of the label.
    keyFor(variant) {
      return (variant.class || variant.label || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
  },
  watch: {
    localValue: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit("update", {
          ...this.localValue,
          typography_variants: this.localValue.typography_variants.map(
            (variant) => ({
              ...variant,
              key: this.keyFor(variant),
            })
          ),
        });
      },
    },
  },
};
</script>

<style scoped lang="scss">
.feature-allowlist {
  margin-bottom: 10px;

  &__header {
    margin-bottom: 8px;
  }

  &__hint {
    font-size: 12px;
    opacity: 0.7;
    margin: 4px 0 0;
  }

  &__actions a {
    font-size: 12px;
    cursor: pointer;
  }
}

.typography-variants {
  margin-top: 10px;

  &__header {
    margin-bottom: 8px;
  }

  &__hint {
    font-size: 12px;
    opacity: 0.7;
    margin: 4px 0 0;
  }
}

.typography-variant {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
</style>
